import { useEffect, useMemo, useRef, useState } from 'react';

import { chalkFontUrl } from './chalkAssets';
import { HandwritingText } from './HandwritingText';

/**
 * "Writing on the board" text reveal. Dispatches to the canonical glyph-outline
 * {@link HandwritingText} when the host injected chalk fonts (setChalkAssets),
 * otherwise streams the flattened text in character-by-character. `onDone` fires
 * when the reveal completes so the host can hand off to a static render.
 */
export function ChalkWriter(props: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  onDone?: () => void;
  paused?: boolean;
  /** Write-on speed multiplier (from the element's handwritingSpeed). */
  speed?: number;
  /** Target duration (ms) — the write-on fills this span (audio-pace sync). */
  targetMs?: number;
}) {
  const { style } = props;
  const family = style?.fontFamily as string | undefined;
  const weight = typeof style?.fontWeight === 'number' ? style.fontWeight : 400;
  const useGlyph = chalkFontUrl(family, weight) != null;
  return useGlyph ? <HandwritingText {...props} /> : <StreamingChalk {...props} />;
}

// ─────────────────────────── lightweight fallback ───────────────────────────

/** Flatten authored HTML to plain text, turning <br>/blocks into newlines. */
function htmlToText(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, '');
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  tmp.querySelectorAll('p, div, li').forEach((el) => el.append('\n'));
  return (tmp.textContent ?? '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/\n+$/g, '');
}

const CHARS_PER_SEC = 84; // 2× handwriting speed (user 2026-08-12)

function StreamingChalk({
  html,
  className,
  style,
  onDone,
  paused,
  targetMs,
}: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  onDone?: () => void;
  paused?: boolean;
  targetMs?: number;
}) {
  const text = useMemo(() => htmlToText(html), [html]);
  const [count, setCount] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    setCount(0);
    doneRef.current = false;
  }, [text]);

  useEffect(() => {
    if (paused) return undefined;
    if (count >= text.length) {
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
      return undefined;
    }
    let raf = 0;
    let last = performance.now();
    let acc = count;
    // Sync to the teacher's pace when a target span is known: cover the whole
    // string in `targetMs` (clamped so it never crawls or blurs); else the
    // fixed brisk default.
    // Max ~11 chars/sec (user 2026-08-16: cap max speed); no min (slow is fine).
    const cps = targetMs && text.length ? Math.max(4, Math.min(11, (text.length / targetMs) * 1000)) : CHARS_PER_SEC;
    const tick = (now: number) => {
      acc += ((now - last) / 1000) * cps;
      last = now;
      const n = Math.min(text.length, Math.floor(acc));
      setCount(n);
      if (n < text.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, count, text, onDone, targetMs]);

  return (
    <div className={className} style={{ ...style, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {text.slice(0, count)}
    </div>
  );
}
